import React, { useState, useEffect } from 'react';
import { apiClient } from '@/utils/api';
import { Package, Users, Calendar, ChevronDown, ChevronUp, ShoppingCart } from 'lucide-react';
import { format, isToday, isTomorrow, addDays, startOfWeek } from 'date-fns';
import toast from 'react-hot-toast';

interface McDonaldsOrder {
  user_id: string;
  user_name: string;
  items: string[];
  special_requests?: string;
  ordered_at: string;
}

interface MealSlot {
  id: string;
  date: string;
  meal_type: string;
  meal_name?: string;
  notes?: string;
}

export function ParentMcDonaldsOrderView() {
  const [mcDonaldsSlots, setMcDonaldsSlots] = useState<MealSlot[]>([]);
  const [selectedSlotOrders, setSelectedSlotOrders] = useState<Record<string, any>>({});
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [weekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));

  useEffect(() => {
    loadMcDonaldsSlots();
  }, []);

  const loadMcDonaldsSlots = async () => {
    setLoading(true);
    try {
      // Load week slots
      const weekSlots = [];
      for (let i = 0; i < 7; i++) {
        const date = format(addDays(weekStart, i), 'yyyy-MM-dd');
        const response = await apiClient.get(`/api/meals/week-plan?start=${date}&end=${date}`);
        if (response && response.length > 0) {
          const dinnerSlot = response.find((s: any) => s.meal_type === 'dinner');
          if (dinnerSlot && dinnerSlot.meal_name && dinnerSlot.meal_name.toLowerCase().includes('mcdonald')) {
            weekSlots.push(dinnerSlot);
            // Load orders for this slot
            await loadOrdersForSlot(dinnerSlot.id);
          }
        }
      }
      setMcDonaldsSlots(weekSlots);
    } catch (error) {
      // Failed to load slots - will show empty state
    } finally {
      setLoading(false);
    }
  };

  const loadOrdersForSlot = async (slotId: string) => {
    try {
      const response = await apiClient.get(`/api/meals/mcdonalds-orders/${slotId}`);
      if (response && response.orders) {
        setSelectedSlotOrders(prev => ({ ...prev, [slotId]: response.orders }));
      }
    } catch (error) {
      // Failed to load orders - continue without them
    }
  };

  const toggleSlotExpansion = (slotId: string) => {
    const newExpanded = new Set(expandedSlots);
    if (newExpanded.has(slotId)) {
      newExpanded.delete(slotId);
    } else {
      newExpanded.add(slotId);
    }
    setExpandedSlots(newExpanded);
  };

  const getDateLabel = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00'); // Add time to avoid timezone issues
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEEE, MMM d');
  };

  const getItemCounts = (orders: Record<string, McDonaldsOrder>) => {
    const counts: Record<string, number> = {};
    Object.values(orders).forEach(order => {
      order.items.forEach(item => {
        counts[item] = (counts[item] || 0) + 1;
      });
    });
    return counts;
  };

  const copyOrdersToClipboard = (slotId: string) => {
    const orders = selectedSlotOrders[slotId];
    if (!orders) return;

    let text = "McDonald's Order:\n\n";
    Object.values(orders as Record<string, McDonaldsOrder>).forEach(order => {
      text += `${order.user_name}:\n`;
      order.items.forEach(item => {
        text += `  • ${item}\n`;
      });
      if (order.special_requests) {
        text += `  Special: ${order.special_requests}\n`;
      }
      text += '\n';
    });

    navigator.clipboard.writeText(text);
    toast.success('Order copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (mcDonaldsSlots.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Package className="w-5 h-5 mr-2 text-red-600" />
          McDonald's Orders
        </h3>
        <p className="text-gray-500 text-center py-4">
          No McDonald's meals scheduled this week
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <Package className="w-5 h-5 mr-2 text-red-600" />
        McDonald's Orders This Week
      </h3>

      <div className="space-y-4">
        {mcDonaldsSlots.map(slot => {
          const orders = selectedSlotOrders[slot.id] || {};
          const orderCount = Object.keys(orders).length;
          const isExpanded = expandedSlots.has(slot.id);
          const itemCounts = getItemCounts(orders);

          return (
            <div key={slot.id} className="border rounded-lg overflow-hidden">
              <div
                className="bg-red-50 p-4 cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => toggleSlotExpansion(slot.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-red-600" />
                    <div>
                      <div className="font-medium">{getDateLabel(slot.date)}</div>
                      <div className="text-sm text-gray-600">
                        {orderCount} {orderCount === 1 ? 'order' : 'orders'} placed
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {orderCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyOrdersToClipboard(slot.id);
                        }}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Copy Order
                      </button>
                    )}
                    {isExpanded ? <ChevronUp /> : <ChevronDown />}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="p-4 bg-white border-t">
                  {orderCount === 0 ? (
                    <p className="text-gray-500 text-center py-4">
                      No orders placed yet. Kids can add their orders from their dashboard.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {/* Individual Orders */}
                      <div className="grid gap-3">
                        {Object.entries(orders as Record<string, McDonaldsOrder>).map(([userId, order]) => (
                          <div key={userId} className="bg-gray-50 rounded-lg p-3">
                            <div className="font-medium text-sm mb-2 flex items-center">
                              <Users className="w-4 h-4 mr-1" />
                              {order.user_name}
                            </div>
                            <div className="space-y-1">
                              {order.items.map((item, idx) => (
                                <div key={idx} className="text-sm text-gray-700 ml-5">
                                  • {item}
                                </div>
                              ))}
                              {order.special_requests && (
                                <div className="text-sm text-gray-600 italic ml-5 mt-2">
                                  Special: {order.special_requests}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Summary */}
                      <div className="border-t pt-3">
                        <div className="font-medium text-sm mb-2 flex items-center">
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Order Summary
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(itemCounts)
                            .sort((a, b) => b[1] - a[1])
                            .map(([item, count]) => (
                              <div key={item} className="text-sm flex justify-between bg-gray-50 px-2 py-1 rounded">
                                <span className="text-gray-700">{item}</span>
                                <span className="font-medium text-red-600">×{count}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}